import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout from '../../components/Layout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { MapPin, Plus } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { label: '', description: '', category: 'Functional Location', superior_functional_location: '', maintenance_plant: '', cost_center: '', status: 'Active' };

const FunctionalLocations = () => {
  const { canWrite } = useAuth();
  const writable = canWrite('functional_location');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await axios.get(`${API}/pm/functional-locations`, { withCredentials: true }); setRows(res.data); }
    catch (e) { toast.error('Failed to load functional locations'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/pm/functional-locations`, {
        ...form,
        superior_functional_location: form.superior_functional_location || null,
        maintenance_plant: form.maintenance_plant || null,
        cost_center: form.cost_center || null,
      }, { withCredentials: true });
      toast.success('Functional location created'); setOpen(false); setForm(EMPTY); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create'); }
    finally { setSaving(false); }
  };

  return (
    <Layout>
      <div className="p-6 space-y-6" data-testid="functional-locations-page">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-sky-100 text-sky-700"><MapPin className="h-5 w-5" /></div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Functional Locations</h1>
              <p className="text-gray-600 text-sm mt-0.5">Hierarchical plant / area / system structure.</p>
            </div>
          </div>
          {writable && <Button onClick={() => { setForm(EMPTY); setOpen(true); }} data-testid="new-floc-btn"><Plus className="h-4 w-4 mr-2" />New Location</Button>}
        </div>

        <Card className="p-0 overflow-hidden">
          <Table>
            <TableHeader><TableRow>
              <TableHead>ID</TableHead><TableHead>Label</TableHead><TableHead>Description</TableHead>
              <TableHead>Category</TableHead><TableHead>Superior</TableHead><TableHead>Plant</TableHead><TableHead>Status</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {loading ? <TableRow><TableCell colSpan={7} className="text-center py-8 text-gray-500">Loading...</TableCell></TableRow>
                : rows.length === 0 ? <TableRow><TableCell colSpan={7} className="text-center py-8 text-gray-500">No functional locations yet.</TableCell></TableRow>
                : rows.map((r) => (
                  <TableRow key={r.functional_location} data-testid={`floc-row-${r.functional_location}`}>
                    <TableCell className="font-medium">{r.functional_location}</TableCell>
                    <TableCell>{r.label}</TableCell>
                    <TableCell>{r.description || '—'}</TableCell>
                    <TableCell>{r.category}</TableCell>
                    <TableCell>{r.superior_functional_location || '—'}</TableCell>
                    <TableCell>{r.maintenance_plant || '—'}</TableCell>
                    <TableCell><Badge variant={r.status === 'Active' ? 'default' : 'secondary'}>{r.status}</Badge></TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </Card>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New Functional Location</DialogTitle>
            <DialogDescription>Define a technical location in the plant structure.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div><Label>Label *</Label><Input data-testid="floc-label" value={form.label} onChange={(e) => set('label', e.target.value)} placeholder="PLANT-A-LINE1" /></div>
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => set('category', v)}>
                <SelectTrigger data-testid="floc-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Site">Site</SelectItem>
                  <SelectItem value="Area">Area</SelectItem>
                  <SelectItem value="System">System</SelectItem>
                  <SelectItem value="Functional Location">Functional Location</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Description</Label><Input data-testid="floc-description" value={form.description} onChange={(e) => set('description', e.target.value)} /></div>
            <div>
              <Label>Superior Location</Label>
              <Select value={form.superior_functional_location || 'none'} onValueChange={(v) => set('superior_functional_location', v === 'none' ? '' : v)}>
                <SelectTrigger data-testid="floc-superior"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None (top level)</SelectItem>
                  {rows.map((r) => <SelectItem key={r.functional_location} value={r.functional_location}>{r.functional_location} — {r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Maintenance Plant</Label><Input data-testid="floc-plant" value={form.maintenance_plant} onChange={(e) => set('maintenance_plant', e.target.value)} placeholder="1000" /></div>
            <div><Label>Cost Center</Label><Input data-testid="floc-costcenter" value={form.cost_center} onChange={(e) => set('cost_center', e.target.value)} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving || !form.label} data-testid="save-floc-btn">{saving ? 'Saving...' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default FunctionalLocations;
